import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Honest social-proof badge. Renders ONLY when a product has real
 * (non-seeded) sales in the last 30 days above a minimum threshold.
 *
 * Dead products → no badge (silence is better than "0 recent sales").
 * Real traction → small badge under the Buy button.
 * Strong traction → "Trending" variant with stronger emphasis.
 *
 * Server is the source of truth for thresholds + label copy — UI
 * just renders whatever `should_show + label` it gets back, so
 * tweaking thresholds doesn't require a frontend redeploy.
 */
export function RecentSalesBadge({ productId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/products/${productId}/recent-sales`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        // Silent: if the endpoint fails, just don't show anything.
        // No badge is always safer than a wrong badge.
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  if (!data || !data.should_show) return null;

  const isTrending = data.tier === 'trending';

  return (
    <div
      data-testid="recent-sales-badge"
      className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
        isTrending
          ? 'bg-orange-500/15 text-orange-300 border border-orange-500/30'
          : 'bg-neutral-800/60 text-neutral-300 border border-white/10'
      }`}
    >
      <Flame
        className={`w-3.5 h-3.5 ${isTrending ? 'text-orange-400' : 'text-neutral-400'}`}
      />
      <span>{data.label}</span>
    </div>
  );
}

export default RecentSalesBadge;
